import * as React from 'react'
import { useLayoutEffect } from 'react'
import type { ReactNode } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { createStackNavigator, StackHeaderProps } from '@react-navigation/stack'
import { useNavigation } from '@react-navigation/native'
import type { StackNavigationProp } from '@react-navigation/stack'
import {
  MelddNavigationBar,
  MelddNavigationBarProps,
} from '../@ui/headers/meldd.header'
import { OfflineModeBanner } from '../@ui/containers/offline-mode.banner'
import { AdminHomeScreen } from '../screens/admin/admin-home.screen'
import { CouponManagementScreen } from '../screens/admin/coupon-management.screen'
import { useAuthContext } from '../contexts/auth-context'
import type { AdminNavigatorParams, AppNavigatorParams } from './types'
import i18n from '../i18n'

const AdminStack = createStackNavigator<AdminNavigatorParams>()

const screenTitle = (name: keyof AdminNavigatorParams) => ({
  title: i18n.t(`screens:${name}`),
})

const renderHeaderWithOfflineBanner = (header: ReactNode) => (
  <View>
    {header}
    <OfflineModeBanner />
  </View>
)

/** Web / deep linking paths under `admin` (see App.tsx linking config). */
export const AdminNavigatorScreens = {
  AdminHome: '',
  CouponManagement: 'coupons',
}

/**
 * Nested stack for super-admin tools. Root `Admin` screen uses `headerShown: false`;
 * this navigator owns headers and ensures only super admins can enter the flow.
 */
export function AdminNavigator() {
  const rootNavigation =
    useNavigation<StackNavigationProp<AppNavigatorParams>>()
  const { isSuperAdmin, loading } = useAuthContext()

  useLayoutEffect(() => {
    if (loading) {
      return
    }
    if (!isSuperAdmin) {
      if (rootNavigation.canGoBack()) {
        return rootNavigation.goBack()
      }
      rootNavigation.navigate('ListNodes')
    }
  }, [isSuperAdmin, loading, rootNavigation])

  if (loading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center' }}>
        <ActivityIndicator />
      </View>
    )
  }

  if (!isSuperAdmin) {
    return null
  }

  return (
    <AdminStack.Navigator initialRouteName="AdminHome">
      <AdminStack.Screen
        name="AdminHome"
        component={AdminHomeScreen}
        options={{
          ...screenTitle('AdminHome'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <MelddNavigationBar {...(props as MelddNavigationBarProps)} />
            ),
        }}
      />
      <AdminStack.Screen
        name="CouponManagement"
        component={CouponManagementScreen}
        options={{
          ...screenTitle('CouponManagement'),
          header: (props: StackHeaderProps) =>
            renderHeaderWithOfflineBanner(
              <MelddNavigationBar
                {...(props as MelddNavigationBarProps)}
                back={{ title: '' }}
              />
            ),
        }}
      />
    </AdminStack.Navigator>
  )
}
